import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import MobileNav from "../components/layout/MobileNav";
import { 
  Mountain, 
  FileText, 
  AlertTriangle, 
  Home, 
  DollarSign,
  LogOut,
  ChevronRight,
  Building2,
  ClipboardCheck,
  MessageSquare,
  User,
  CreditCard,
  Map,
  Megaphone,
  HelpCircle
} from "lucide-react";

const OwnerDashboard = () => {
  const { user, token, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, profileRes] = await Promise.all([
          axios.get(`${API}/dashboard/stats`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/owners/me`, { headers: { Authorization: `Bearer ${token}` } })
        ]);
        setStats(statsRes.data);
        if (profileRes.data.has_profile !== false) {
          setProfile(profileRes.data);
        }
      } catch (error) {
        console.error("Failed to fetch data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  const quickLinks = [
    {
      icon: <User className="w-5 h-5" />,
      title: "My Profile",
      description: "View and edit your information",
      href: "/owner/profile",
      color: "text-primary"
    },
    {
      icon: <CreditCard className="w-5 h-5" />,
      title: "Payments",
      description: "View and pay dues",
      href: "/owner/payments",
      color: "text-green-600"
    },
    {
      icon: <FileText className="w-5 h-5" />,
      title: "Documents",
      description: "View CC&Rs, Bylaws, and Guidelines",
      href: "/owner/documents",
      color: "text-blue-600"
    },
    {
      icon: <AlertTriangle className="w-5 h-5" />,
      title: "Notices",
      description: "Check notice status",
      href: "/owner/violations",
      color: "text-destructive"
    },
    {
      icon: <ClipboardCheck className="w-5 h-5" />,
      title: "Design Review",
      description: "Submit DRC applications",
      href: "/owner/drc",
      color: "text-orange-600"
    },
    {
      icon: <MessageSquare className="w-5 h-5" />,
      title: "Community Forum",
      description: "Discussions & Q&A",
      href: "/owner/forum",
      color: "text-purple-600"
    },
    {
      icon: <Map className="w-5 h-5" />,
      title: "GIS Property Map",
      description: "View property boundaries",
      href: "/owner/gis",
      color: "text-teal-600"
    },
    {
      icon: <Megaphone className="w-5 h-5" />,
      title: "Announcements",
      description: "Community updates",
      href: "/owner/announcements",
      color: "text-indigo-600"
    },
    {
      icon: <HelpCircle className="w-5 h-5" />,
      title: "Help Center",
      description: "FAQs and self-service help",
      href: "/owner/help",
      color: "text-cyan-600"
    }
  ];

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-0">
      {/* Header - Hidden on mobile, shown on desktop */}
      <header className="border-b bg-card hidden md:block">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Mountain className="w-7 h-7 text-copper" />
            <div>
              <span className="font-serif text-lg font-semibold">Horse Mountain Ranch</span>
              <span className="text-muted-foreground text-sm ml-2">Owner Portal</span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-muted-foreground hidden sm:inline">
              {user?.email}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={logout}
              data-testid="logout-btn"
              className="text-muted-foreground hover:text-foreground transition-colors duration-200"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </div>
      </header>

      {/* Mobile Header */}
      <header className="border-b bg-card md:hidden sticky top-0 z-40">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Mountain className="w-6 h-6 text-copper" />
            <span className="font-serif text-base font-semibold">Horse Mountain</span>
          </div>
          <Link to="/owner/profile">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
              <User className="w-4 h-4 text-primary" />
            </div>
          </Link>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-8 py-4 md:py-8">
        {/* Profile Alert - Show if profile incomplete */}
        {!loading && (!profile || !profile.phone_primary || !profile.mailing_address) && (
          <div className="mb-6 p-4 bg-warning/10 border border-warning/20 rounded-xl flex items-start gap-4 animate-fade-in-up">
            <AlertTriangle className="w-5 h-5 text-warning mt-0.5" />
            <div className="flex-1">
              <p className="font-semibold text-warning">Complete Your Profile</p>
              <p className="text-sm text-muted-foreground mt-1">
                {!profile 
                  ? "Your account is not linked to an owner profile. Please contact the board."
                  : "Please update your profile with your phone number and mailing address."}
              </p>
            </div>
            <Link to="/owner/profile">
              <Button size="sm" variant="outline" className="border-warning/30 text-warning hover:bg-warning/10 hover:border-warning/50 transition-all duration-200">
                {profile ? "Update Profile" : "View Details"}
              </Button>
            </Link>
          </div>
        )}

        {/* Welcome Section */}
        <div className="mb-6 md:mb-8 animate-fade-in-up">
          <h1 className="font-serif text-2xl md:text-4xl font-bold text-foreground mb-1 md:mb-2">
            Welcome, {user?.name?.split(' ')[0] || 'Owner'}
          </h1>
          <p className="text-muted-foreground text-sm md:text-lg">
            Manage your property and stay connected with your community.
          </p>
        </div>

        {/* Stats Grid - Mobile optimized */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6 mb-6 md:mb-8">
          <Card data-testid="stat-properties" className="opacity-0 animate-fade-in-up border-border/50 hover:border-border hover:shadow-floating transition-all duration-300 group" style={{ animationDelay: '0.1s' }}>
            <CardContent className="p-4 md:pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs md:text-sm text-muted-foreground">Properties</p>
                  <p className="text-2xl md:text-3xl font-bold text-foreground mt-0.5 md:mt-1">
                    {loading ? <span className="skeleton-luxury inline-block w-8 h-8 rounded" /> : stats?.my_properties || 0}
                  </p>
                </div>
                <div className="w-10 h-10 md:w-12 md:h-12 bg-primary/10 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <Building2 className="w-5 h-5 md:w-6 md:h-6 text-primary" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="stat-violations" className="opacity-0 animate-fade-in-up border-border/50 hover:border-border hover:shadow-floating transition-all duration-300 group" style={{ animationDelay: '0.2s' }}>
            <CardContent className="p-4 md:pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs md:text-sm text-muted-foreground">Notices</p>
                  <p className="text-2xl md:text-3xl font-bold text-foreground mt-0.5 md:mt-1">
                    {loading ? <span className="skeleton-luxury inline-block w-8 h-8 rounded" /> : stats?.my_violations || 0}
                  </p>
                </div>
                <div className="w-10 h-10 md:w-12 md:h-12 bg-destructive/10 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <AlertTriangle className="w-5 h-5 md:w-6 md:h-6 text-destructive" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="stat-drc" className="opacity-0 animate-fade-in-up border-border/50 hover:border-border hover:shadow-floating transition-all duration-300 group" style={{ animationDelay: '0.3s' }}>
            <CardContent className="p-4 md:pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs md:text-sm text-muted-foreground">DRC</p>
                  <p className="text-2xl md:text-3xl font-bold text-foreground mt-0.5 md:mt-1">
                    {loading ? <span className="skeleton-luxury inline-block w-8 h-8 rounded" /> : stats?.my_drc_submissions || 0}
                  </p>
                </div>
                <div className="w-10 h-10 md:w-12 md:h-12 bg-secondary/10 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <ClipboardCheck className="w-5 h-5 md:w-6 md:h-6 text-secondary" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="stat-dues" className="opacity-0 animate-fade-in-up border-border/50 hover:border-border hover:shadow-floating transition-all duration-300 group" style={{ animationDelay: '0.4s' }}>
            <CardContent className="p-4 md:pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs md:text-sm text-muted-foreground">Balance</p>
                  <p className="text-2xl md:text-3xl font-bold text-foreground mt-0.5 md:mt-1">
                    ${loading ? <span className="skeleton-luxury inline-block w-12 h-8 rounded" /> : (stats?.dues_balance || 0).toFixed(0)}
                  </p>
                </div>
                <div className="w-10 h-10 md:w-12 md:h-12 bg-chart-4/20 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <DollarSign className="w-5 h-5 md:w-6 md:h-6 text-chart-4" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Links - Scrollable on mobile */}
        <div className="mb-6 md:mb-8">
          <h2 className="font-semibold text-foreground mb-3 md:mb-4">Quick Actions</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-6">
          {quickLinks.map((link, index) => (
            <Link key={index} to={link.href}>
              <Card 
                data-testid={`quick-link-${index}`}
                className="h-full hover:-translate-y-1 hover:shadow-floating transition-all duration-300 cursor-pointer group border-border/50 hover:border-border active:scale-[0.98]"
              >
                <CardContent className="p-3 md:pt-6">
                  <div className="flex items-start justify-between">
                    <div className={`w-9 h-9 md:w-10 md:h-10 rounded-xl flex items-center justify-center bg-current/10 group-hover:scale-110 transition-transform duration-300`}>
                      <span className={link.color}>{link.icon}</span>
                    </div>
                    <ChevronRight className="w-4 h-4 md:w-5 md:h-5 text-muted-foreground group-hover:text-foreground group-hover:translate-x-1 transition-all duration-300" />
                  </div>
                  <h3 className="font-semibold text-foreground text-sm md:text-base mt-3 md:mt-4 mb-0.5 md:mb-1">{link.title}</h3>
                  <p className="text-xs md:text-sm text-muted-foreground line-clamp-2">{link.description}</p>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
        </div>

        {/* Recent Activity / Announcements */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card data-testid="announcements-card" className="border-border/50">
            <CardHeader>
              <CardTitle className="font-serif text-xl">Community Announcements</CardTitle>
              <CardDescription>Latest updates from the HOA board</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="p-4 bg-muted/50 rounded-xl hover:bg-muted/70 transition-colors duration-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge variant="secondary" className="badge-info">New</Badge>
                    <span className="text-xs text-muted-foreground">Dec 15, 2024</span>
                  </div>
                  <p className="text-sm text-foreground">
                    Annual meeting scheduled for January 15th. All owners are encouraged to attend.
                  </p>
                </div>
                <div className="p-4 bg-muted/50 rounded-xl hover:bg-muted/70 transition-colors duration-200">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs text-muted-foreground">Dec 1, 2024</span>
                  </div>
                  <p className="text-sm text-foreground">
                    Winter maintenance guidelines have been updated. Please review the new snow removal policies.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="helpful-resources-card">
            <CardHeader>
              <CardTitle className="font-serif text-xl">Helpful Resources</CardTitle>
              <CardDescription>Quick access to important documents</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <Link 
                  to="/owner/documents"
                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors"
                >
                  <FileText className="w-5 h-5 text-primary" />
                  <div>
                    <p className="text-sm font-medium">CC&Rs</p>
                    <p className="text-xs text-muted-foreground">Covenants, Conditions & Restrictions</p>
                  </div>
                </Link>
                <Link 
                  to="/owner/documents"
                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors"
                >
                  <FileText className="w-5 h-5 text-primary" />
                  <div>
                    <p className="text-sm font-medium">Design Guidelines</p>
                    <p className="text-xs text-muted-foreground">Architectural standards and requirements</p>
                  </div>
                </Link>
                <Link 
                  to="/owner/documents"
                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors"
                >
                  <FileText className="w-5 h-5 text-primary" />
                  <div>
                    <p className="text-sm font-medium">Bylaws</p>
                    <p className="text-xs text-muted-foreground">HOA governance and procedures</p>
                  </div>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Mobile Navigation */}
      <MobileNav />
    </div>
  );
};

export default OwnerDashboard;
